import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Loader, User, MapPin, Phone, MailCheck } from 'lucide-react';
import { apiPost, errorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Turnstile } from '../components/Turnstile';
import { TURNSTILE_SITE_KEY } from '../lib/turnstile';
import { AddressCombobox } from '../components/AddressCombobox';
import { summaryLabel } from '../lib/addresses';
import { moveInYears, yearToMoveInDate } from '../lib/dates';
import type { AddressSummary, HouseholdClaim } from '../types';
import './Auth.css';

interface AuthProps {
    initialMode?: 'signin' | 'signup';
}

type AuthMode = 'signin' | 'signup';

const MIN_PASSWORD = 8;

/** Built once: the list is the same for every render and every visitor. */
const MOVE_IN_YEARS = moveInYears();

interface Strength { score: 0 | 1 | 2 | 3; label: string }

function passwordStrength(pw: string): Strength {
    if (!pw) return { score: 0, label: '' };
    let points = 0;
    if (pw.length >= MIN_PASSWORD) points++;
    if (pw.length >= 12) points++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) points++;
    if (/\d/.test(pw)) points++;
    if (/[^A-Za-z0-9]/.test(pw)) points++;
    if (pw.length < MIN_PASSWORD) return { score: 1, label: `Too short — at least ${MIN_PASSWORD} characters` };
    if (points <= 2) return { score: 1, label: 'Weak — add numbers or symbols' };
    if (points <= 3) return { score: 2, label: 'Okay' };
    return { score: 3, label: 'Strong' };
}

/** The Worker already answers with a sentence for the resident, so there is
 *  nothing to translate — only a fallback for a dropped connection. */
const authFallback = (mode: AuthMode) =>
    mode === 'signin' ? "Couldn't sign you in. Please try again." : "Couldn't create your account. Please try again.";

export function Auth({ initialMode = 'signin' }: AuthProps) {
    const navigate = useNavigate();
    const { refreshProfile } = useAuth();
    const location = useLocation();
    // The URL is the source of truth so /login and /signup both work even when
    // the router reuses this component instance.
    const mode: AuthMode = location.pathname === '/signup' ? 'signup' : location.pathname === '/login' ? 'signin' : initialMode;
    const from = (location.state as { from?: string } | null)?.from;

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [streetAddress, setStreetAddress] = useState('');
    const [address, setAddress] = useState<AddressSummary | null>(null);
    // "My address isn't listed" — keeps the old free-text field so nobody is
    // locked out by a gap in the registry.
    const [manualAddress, setManualAddress] = useState(false);
    const [householdClaim, setHouseholdClaim] = useState<Exclude<HouseholdClaim, 'new'> | null>(null);
    const [householdMessage, setHouseholdMessage] = useState('');
    const [phone, setPhone] = useState('');
    // Empty rather than the current year. A prefilled year is an answer the
    // resident never gave, and a wrong one drops them into the wrong tenure
    // tier without anyone noticing.
    const [residentSinceYear, setResidentSinceYear] = useState('');
    const [tenureType, setTenureType] = useState<'rent' | 'own'>('own');
    const [consent, setConsent] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaReset, setCaptchaReset] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);
    const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

    const switchMode = (next: AuthMode) => {
        setError(null);
        navigate(next === 'signup' ? '/signup' : '/login', { replace: true, state: from ? { from } : undefined });
    };

    const strength = passwordStrength(password);
    const captchaRequired = TURNSTILE_SITE_KEY.length > 0;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        if (mode === 'signup') {
            if (!manualAddress && !address) { setError('Please choose your address from the list, or tell us it is not listed.'); return; }
            if (manualAddress && !streetAddress.trim()) { setError('Please type your street address.'); return; }
            if (address?.registered && !householdClaim) { setError('Please tell us whether you live with the people already registered there.'); return; }
            if (!residentSinceYear) { setError('Please tell us which year you moved in.'); return; }
            if (password.length < MIN_PASSWORD) { setError(`Your password needs at least ${MIN_PASSWORD} characters.`); return; }
            if (!consent) { setError('Please agree to the Privacy Notice to continue.'); return; }
            if (captchaRequired && !captchaToken) { setError('Please complete the verification check.'); return; }
        }

        setLoading(true);
        try {
            if (mode === 'signin') {
                await apiPost('/api/auth/signin', { email: email.trim(), password });
                // The session is an HttpOnly cookie, so AuthContext still thinks
                // nobody is signed in. Make it re-read /api/auth/me before we
                // navigate, or the router bounces us straight back to /login.
                await refreshProfile();
                navigate(from ?? '/', { replace: true });
            } else {
                try {
                    await apiPost('/api/auth/signup', {
                        email: email.trim(),
                        password,
                        full_name: fullName.trim(),
                        // The id is what links the household; the free text stays
                        // for backward compatibility and for "not listed" sign-ups.
                        address_id: address?.id,
                        street_address: address ? summaryLabel(address) : streetAddress.trim(),
                        household_claim: address ? (address.registered ? householdClaim ?? undefined : 'new') : undefined,
                        household_message: address?.registered && householdMessage.trim() ? householdMessage.trim() : undefined,
                        phone: phone.trim() || undefined,
                        resident_since: yearToMoveInDate(residentSinceYear),
                        tenure_type: tenureType,
                        consent: true,
                        turnstile_token: captchaRequired ? captchaToken : undefined,
                    });
                } catch (signUpError) {
                    // A fresh challenge is needed for the next attempt.
                    setCaptchaToken('');
                    setCaptchaReset(n => n + 1);
                    throw signUpError;
                }
                setSignedUpEmail(email.trim());
                setPassword('');
            }
        } catch (err) {
            setError(errorMessage(err, authFallback(mode)));
        } finally {
            setLoading(false);
        }
    };

    const resendConfirmation = async () => {
        if (!signedUpEmail || resendState !== 'idle') return;
        setResendState('sending');
        try {
            await apiPost('/api/auth/resend-verification', { email: signedUpEmail });
        } catch (err) {
            setError(errorMessage(err, "Couldn't resend right now. Please try again in a minute."));
            setResendState('idle');
            return;
        }
        setResendState('sent');
        window.setTimeout(() => setResendState('idle'), 60_000);
    };

    if (signedUpEmail) {
        return (
            <div className="auth-page">
                <div className="auth-container animate-fade-in">
                    <div className="auth-header">
                        <div className="auth-logo auth-logo-success"><MailCheck size={32} /></div>
                        <h1>Check your email</h1>
                        <p>We sent a confirmation link to <strong>{signedUpEmail}</strong>.</p>
                    </div>
                    <div className="auth-success-panel">
                        <p>Check your email to confirm your address. After that an admin will verify you live in Forest Town (usually 1–2 days).</p>
                        {householdClaim === 'replace' && address && (
                            <p>We've passed your request to take over {summaryLabel(address)} to the committee. They'll update the household record once they've checked it.</p>
                        )}
                        {householdClaim === 'family' && address && (
                            <p>You'll join the household at {summaryLabel(address)} as soon as you're approved.</p>
                        )}
                        <p className="text-muted">Can't find it? Look in your spam or promotions folder.</p>
                    </div>
                    {error && <div className="auth-error animate-fade-in" role="alert">{error}</div>}
                    <div className="auth-actions">
                        <button
                            type="button"
                            className="btn btn-secondary btn-full"
                            onClick={resendConfirmation}
                            disabled={resendState !== 'idle'}
                        >
                            {resendState === 'sending' && <Loader className="spinner-icon" size={18} />}
                            {resendState === 'sent' ? 'Sent — check your inbox' : resendState === 'sending' ? 'Sending…' : 'Resend confirmation email'}
                        </button>
                        <Link to="/login" className="btn btn-ghost btn-full">Back to sign in</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-container animate-fade-in">
                <button type="button" className="auth-back" onClick={() => navigate('/')}>
                    <ArrowLeft size={20} />
                    Back
                </button>

                <div className="auth-header">
                    <img
                        className="auth-brand"
                        src="/brand/logo.svg"
                        alt="Forest Town Ratepayers' Association"
                        width={220}
                        height={123}
                    />
                    <h1>{mode === 'signin' ? 'Welcome back' : 'Join Forest Town'}</h1>
                    <p>{mode === 'signin' ? 'Sign in to your account' : 'Create your resident account'}</p>
                </div>

                <form className="auth-form" onSubmit={handleSubmit} noValidate={false}>
                    {error && (
                        <div className="auth-error animate-fade-in" role="alert">
                            {error}
                        </div>
                    )}

                    {mode === 'signup' && (
                        <>
                            <div className="form-group">
                                <label className="label" htmlFor="fullName">Full name</label>
                                <div className="input-wrapper">
                                    <User className="input-icon" size={20} aria-hidden="true" />
                                    <input
                                        id="fullName"
                                        type="text"
                                        className="input input-with-icon"
                                        placeholder="Your full name"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        required
                                        maxLength={120}
                                        autoComplete="name"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="label" htmlFor="address">Street address in Forest Town</label>
                                {manualAddress ? (
                                    <>
                                        <div className="input-wrapper">
                                            <MapPin className="input-icon" size={20} aria-hidden="true" />
                                            <input
                                                id="address"
                                                type="text"
                                                className="input input-with-icon"
                                                placeholder="42 Oak Street"
                                                value={streetAddress}
                                                onChange={(e) => setStreetAddress(e.target.value)}
                                                required
                                                maxLength={200}
                                                autoComplete="street-address"
                                                aria-describedby="address-hint"
                                            />
                                        </div>
                                        <span className="form-hint" id="address-hint">
                                            The committee will add your address to the list when they check your application.
                                        </span>
                                        <button
                                            type="button"
                                            className="address-manual-toggle"
                                            onClick={() => { setManualAddress(false); setStreetAddress(''); }}
                                        >
                                            Search the address list instead
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <AddressCombobox
                                            id="address"
                                            value={address}
                                            describedBy="address-hint"
                                            onSelect={(next) => {
                                                setAddress(next);
                                                setHouseholdClaim(null);
                                                setHouseholdMessage('');
                                            }}
                                        />
                                        <span className="form-hint" id="address-hint">
                                            Used only to verify you live here. Not shown to other residents.
                                        </span>
                                        <button
                                            type="button"
                                            className="address-manual-toggle"
                                            onClick={() => { setManualAddress(true); setAddress(null); setHouseholdClaim(null); }}
                                        >
                                            My address isn't listed
                                        </button>
                                    </>
                                )}

                                {address?.registered && (
                                    <fieldset className="household-branch">
                                        <legend>Someone is already registered at {summaryLabel(address)}</legend>
                                        <p>
                                            That's normal — one household can have several members on the portal.
                                            Which of these is you?
                                        </p>
                                        <label className={`household-choice ${householdClaim === 'family' ? 'is-selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="householdClaim"
                                                value="family"
                                                checked={householdClaim === 'family'}
                                                onChange={() => setHouseholdClaim('family')}
                                            />
                                            <span>
                                                Yes, I live with them
                                                <span className="household-choice-hint">You'll be added to the same household.</span>
                                            </span>
                                        </label>
                                        <label className={`household-choice ${householdClaim === 'replace' ? 'is-selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="householdClaim"
                                                value="replace"
                                                checked={householdClaim === 'replace'}
                                                onChange={() => setHouseholdClaim('replace')}
                                            />
                                            <span>
                                                No, I'm the new owner or tenant
                                                <span className="household-choice-hint">The committee will update the household record.</span>
                                            </span>
                                        </label>

                                        {householdClaim === 'replace' && (
                                            <>
                                                <div className="form-group">
                                                    <label className="label" htmlFor="householdTenure">At this address I…</label>
                                                    <select
                                                        id="householdTenure"
                                                        className="select"
                                                        value={tenureType}
                                                        onChange={(e) => setTenureType(e.target.value as 'rent' | 'own')}
                                                    >
                                                        <option value="own">Own the property</option>
                                                        <option value="rent">Rent the property</option>
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <label className="label" htmlFor="householdMessage">
                                                        Anything the committee should know <span className="label-optional">(optional)</span>
                                                    </label>
                                                    <textarea
                                                        id="householdMessage"
                                                        className="textarea"
                                                        rows={3}
                                                        maxLength={400}
                                                        placeholder="e.g. I moved in on 1 March and the previous tenants have left."
                                                        value={householdMessage}
                                                        onChange={(e) => setHouseholdMessage(e.target.value)}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        <p>
                                            Either way the committee checks every account by hand before it is switched on.
                                        </p>
                                    </fieldset>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="label" htmlFor="phone">Phone <span className="label-optional">(optional)</span></label>
                                <div className="input-wrapper">
                                    <Phone className="input-icon" size={20} aria-hidden="true" />
                                    <input
                                        id="phone"
                                        type="tel"
                                        className="input input-with-icon"
                                        placeholder="082 123 4567"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        maxLength={30}
                                        autoComplete="tel"
                                        inputMode="tel"
                                    />
                                </div>
                                <span className="form-hint">South African format, e.g. 082 123 4567 or +27 82 123 4567.</span>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="label" htmlFor="residentSince">Year you moved in</label>
                                    <select
                                        id="residentSince"
                                        className="select"
                                        value={residentSinceYear}
                                        onChange={(e) => setResidentSinceYear(e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>Select a year</option>
                                        {MOVE_IN_YEARS.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="label" htmlFor="tenureType">I am a…</label>
                                    <select
                                        id="tenureType"
                                        className="select"
                                        value={tenureType}
                                        onChange={(e) => setTenureType(e.target.value as 'rent' | 'own')}
                                        required
                                    >
                                        <option value="own">Homeowner</option>
                                        <option value="rent">Renter</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <label className="label" htmlFor="email">Email address</label>
                        <div className="input-wrapper">
                            <Mail className="input-icon" size={20} aria-hidden="true" />
                            <input
                                id="email"
                                type="email"
                                className="input input-with-icon"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                inputMode="email"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="label-row">
                            <label className="label" htmlFor="password">Password</label>
                            {mode === 'signin' && (
                                <Link to="/forgot-password" className="auth-inline-link">Forgot password?</Link>
                            )}
                        </div>
                        <div className="input-wrapper">
                            <Lock className="input-icon" size={20} aria-hidden="true" />
                            <input
                                id="password"
                                type="password"
                                className="input input-with-icon"
                                placeholder={mode === 'signup' ? `At least ${MIN_PASSWORD} characters` : '••••••••'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                                minLength={mode === 'signup' ? MIN_PASSWORD : undefined}
                                aria-describedby={mode === 'signup' ? 'password-strength' : undefined}
                            />
                        </div>
                        {mode === 'signup' && (
                            <div id="password-strength" className={`password-strength strength-${strength.score}`} aria-live="polite">
                                <div className="strength-bar"><span /></div>
                                <span className="form-hint">{strength.label || `At least ${MIN_PASSWORD} characters. Mix letters, numbers and symbols for a stronger one.`}</span>
                            </div>
                        )}
                    </div>

                    {mode === 'signup' && (
                        <>
                            <label className="consent-row">
                                <input
                                    type="checkbox"
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    required
                                />
                                <span>
                                    I agree to the <Link to="/privacy" target="_blank" rel="noopener">Privacy Notice</Link> and to my name, address and contact details
                                    being stored so the Forest Town Residents' Association can verify I live here and contact me about community matters.
                                </span>
                            </label>

                            {captchaRequired && (
                                <Turnstile onToken={setCaptchaToken} resetKey={captchaReset} />
                            )}
                        </>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary btn-full"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Loader className="spinner-icon" size={20} />
                                {mode === 'signin' ? 'Signing in…' : 'Creating account…'}
                            </>
                        ) : (
                            mode === 'signin' ? 'Sign in' : 'Create account'
                        )}
                    </button>
                </form>

                <div className="auth-footer">
                    {mode === 'signin' ? (
                        <p>
                            New to the portal?{' '}
                            <button type="button" className="auth-link" onClick={() => switchMode('signup')}>
                                Sign up
                            </button>
                        </p>
                    ) : (
                        <p>
                            Already have an account?{' '}
                            <button type="button" className="auth-link" onClick={() => switchMode('signin')}>
                                Sign in
                            </button>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
